<!--
  qmContactField: "QM-Kontakt" of an Ansprechpartner (responsibles) as a profile row with checkbox (field qmContact). Default:
  the QM (calls in the Akte and feedback requests) contacts only the Standort itself; marked Ansprechpartner replace it
  (unreachable ones → the Standort again). The institution-level flag is no longer shown (ignored by the rule). The parent shows
  it only with read access on the field (v-can … keys ['qmContact']); without update access the checkbox is disabled.
  Props: entity ({_id, qmContact}), entityType (institutions | responsibles)
-->
<template>
  <profile-field :icon="marked ? 'mdi-account-star-outline' : 'mdi-account-outline'" label="QM-Kontakt">
    <div class="fld__value">{{ marked ? 'ja – das QM kontaktiert diese Person statt des Standorts' : 'nein' }}</div>
    <div class="fld__note">Ohne Markierung ruft und schreibt das QM nur den Standort selbst an (Mail / Telefon des Standorts). Sind Ansprechpartner:innen als
      QM-Kontakt markiert, stattdessen genau diese (ohne Mail / Telefon wieder der Standort).</div>
    <template #actions>
      <span :class="{ 'app-tip-wrap': !canUpdate }" v-tip="canUpdate ? '' : 'Dir fehlt das Recht, diese Einstellung zu ändern.'">
        <v-checkbox :key="renderKey" :disabled="!canUpdate || saving" :input-value="marked" aria-label="QM-Kontakt" class="mt-0 pt-0" color="info" dense hide-details
                    @change="save"/>
      </span>
    </template>
  </profile-field>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import ProfileField from '@/components/common/profileUi/ProfileField.vue';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {ResponsibleHandlerAPI} from '@/classes/api/responsible-handler.api.class';

const FIELD = 'qmContact';

export default Vue.extend({
  components: {ProfileField},
  props: {
    entity: {type: Object as () => { _id: Types.ObjectId; qmContact?: boolean }, required: true},
    entityType: {type: String as () => CrudEntityTypes.INSTITUTION | CrudEntityTypes.RESPONSIBLE, required: true},
  },
  data: () => ({
    saving: false,
    localValue: undefined as boolean | undefined,
    renderKey: 0,
  }),
  computed: {
    marked(): boolean {
      return this.localValue ?? !!this.entity.qmContact;
    },
    canUpdate(): boolean {
      return this.$$crudAccessManager.canAccessEntity(this.entityType, CrudActionTypes.UPDATE, this.entity._id, FIELD);
    },
  },
  methods: {
    async save(value: boolean) {
      const next = !!value;
      this.saving = true;
      try {
        const body = {qmContact: next};
        if (this.entityType === CrudEntityTypes.INSTITUTION) {
          await InstitutionerApi.update(this.entity._id, body);
        } else {
          await ResponsibleHandlerAPI.update(this.entity._id, body);
        }
        this.localValue = next;
        this.$emit('changed', next);
        this.$$showSnackbar(next ? 'Als QM-Kontakt markiert' : 'Nicht mehr QM-Kontakt', 'success');
      } catch (e) {
        this.renderKey++;
        this.$$showSnackbar('Fehler beim Speichern der Einstellung', 'error', e);
      } finally {
        this.saving = false;
      }
    },
  },
  watch: {
    'entity.qmContact'() {
      this.localValue = undefined;
    },
  },
});
</script>
